<script>
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Layout from '../components/Layout.svelte';
  import { customersApi, customerPricesApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import { ArrowLeft, User, Mail, Phone, MapPin, Building2, CreditCard, Plus, Edit2, Trash2, Tag, DollarSign, UserCheck, Key, AlertCircle } from 'lucide-svelte';
  import { isAdmin } from '../stores/roles.js';

  export let params = {};

  // Same vocabulary, same order as CustomerPricesModal on the products side:
  // the plain negotiated price plus the four absolute hourly surcharges.
  const PRICE_CONDITIONS = ['base', 'weekend', 'holiday', 'urgent', 'night'];

  let customer = null;
  let customerPrices = [];
  let loading = true;
  let error = '';
  let activeTab = 'info';

  // Edit price modal
  let showPriceModal = false;
  let editingPrice = null;
  let priceFormData = {
    variantId: '',
    customPrice: '',
    notes: ''
  };
  let savingPrice = false;
  let priceError = '';

  // User account state
  let userInfo = null;
  let loadingUserInfo = false;
  let showCreateUserModal = false;
  let newPassword = '';
  let confirmPassword = '';
  let creatingUser = false;
  let userError = '';
  let userSuccess = '';

  onMount(() => {
    if (params.id) {
      loadCustomer();
    } else {
      error = 'Customer ID not provided';
      loading = false;
    }
  });

  async function loadCustomer() {
    try {
      loading = true;
      error = '';

      // Load customer details
      const customerData = await customersApi.getById(params.id);
      customer = customerData;
      console.log('👤 Loaded customer:', customer);

      // Load customer's custom prices
      const pricesData = await customerPricesApi.getByCustomer(params.id);
      customerPrices = pricesData;
      console.log('💰 Loaded customer prices:', customerPrices.length);

      // Load user info
      await loadUserInfo();

    } catch (err) {
      error = err.message;
      console.error('❌ Load customer error:', err);
    } finally {
      loading = false;
    }
  }

  async function loadUserInfo() {
    try {
      loadingUserInfo = true;
      userInfo = await customersApi.getUserInfo(params.id);
      console.log('👤 User info loaded:', userInfo);
    } catch (err) {
      console.error('❌ Failed to load user info:', err);
      userInfo = null;
    } finally {
      loadingUserInfo = false;
    }
  }

  function goBack() {
    push('/customers');
  }

  function getDisplayName() {
    if (!customer) return '';
    if (customer.companyName) {
      return customer.companyName;
    }
    return [customer.name, customer.surname].filter(Boolean).join(' ') || customer.id;
  }

  function formatPrice(price) {
    if (price === null || price === undefined) return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '-';
    return `€${numPrice.toFixed(2)}`;
  }

  function calculateDiscount(basePrice, customPrice) {
    if (!basePrice || !customPrice) return null;
    const base = parseFloat(basePrice);
    const custom = parseFloat(customPrice);
    if (isNaN(base) || isNaN(custom) || base === 0) return null;
    return ((1 - custom / base) * 100).toFixed(0);
  }

  /**
   * Condition a price row applies to. Rows created before migration 060 carry
   * no conditionCode and are the plain negotiated price, i.e. 'base'.
   * @param {object} price
   * @returns {string}
   */
  function conditionOf(price) {
    return price.conditionCode || 'base';
  }

  /**
   * The plain negotiated price, as opposed to one of the four hourly
   * surcharges. Only this row compares against the variant's list price (a
   * surcharge is deliberately *higher* than the base, so a "% off" on it would
   * render a negative discount in green), and only this row carries notes -
   * the same split CustomerPricesModal enforces when it writes them.
   * @param {object} price
   * @returns {boolean}
   */
  function isBasePrice(price) {
    return conditionOf(price) === 'base';
  }

  /** Condition label, from the same keys the products-side modal uses. */
  function conditionLabel(price) {
    return $_(`products.variants.conditionPrices.${conditionOf(price)}`);
  }

  /** How a variant is named in confirmations and headings. */
  function variantLabel(price) {
    return price.variant?.code || String(price.variantId);
  }

  // One row per (variant, condition): the API returns every condition row for
  // the customer, so a single hourly variant legitimately appears up to five
  // times. Sorting groups a variant's rows together with its base price first,
  // which is the order CustomerPricesModal lays the same five values out in.
  $: sortedPrices = [...customerPrices].sort((a, b) => {
    const byVariant = variantLabel(a).localeCompare(variantLabel(b));
    if (byVariant !== 0) return byVariant;
    return PRICE_CONDITIONS.indexOf(conditionOf(a)) - PRICE_CONDITIONS.indexOf(conditionOf(b));
  });

  function openEditPriceModal(price) {
    editingPrice = price;
    priceFormData = {
      variantId: price.variantId,
      customPrice: String(price.customPrice),
      notes: price.notes || ''
    };
    priceError = '';
    showPriceModal = true;
  }

  function closePriceModal() {
    showPriceModal = false;
    editingPrice = null;
    priceFormData = { variantId: '', customPrice: '', notes: '' };
    priceError = '';
  }

  async function handleSavePrice(event) {
    event.preventDefault();

    try {
      savingPrice = true;
      priceError = '';

      const price = parseFloat(priceFormData.customPrice.replace(',', '.'));
      if (isNaN(price) || price < 0) {
        priceError = 'Please enter a valid price';
        return;
      }

      // Notes live on the base row only, exactly as CustomerPricesModal
      // writes them: sending notes for a condition row would put the same
      // information in five places and make the two surfaces disagree about
      // where it belongs.
      const payload = { customPrice: price };
      if (isBasePrice(editingPrice)) {
        payload.notes = priceFormData.notes || null;
      }

      await customerPricesApi.update(editingPrice.id, payload);

      console.log('✅ Price updated');
      await loadCustomer();
      closePriceModal();

    } catch (err) {
      priceError = err.message;
      console.error('❌ Save price error:', err);
    } finally {
      savingPrice = false;
    }
  }

  /**
   * Deletes one price row. The confirmation names the variant and the
   * condition, because up to five rows of the same variant sit next to each
   * other and a generic "this custom price" gives no way to tell which one is
   * about to go.
   * @param {object} price
   */
  async function deletePrice(price) {
    const question = $_('customers.prices.deleteConfirm', {
      values: { condition: conditionLabel(price), variant: variantLabel(price) }
    });
    if (!confirm(question)) {
      return;
    }

    try {
      await customerPricesApi.delete(price.id);
      console.log('🗑️ Price deleted');
      await loadCustomer();
    } catch (err) {
      error = err.message;
      console.error('❌ Delete price error:', err);
    }
  }

  function openCreateUserModal() {
    newPassword = '';
    confirmPassword = '';
    userError = '';
    userSuccess = '';
    showCreateUserModal = true;
  }

  function closeCreateUserModal() {
    showCreateUserModal = false;
    newPassword = '';
    confirmPassword = '';
    userError = '';
  }

  async function handleCreateUser(event) {
    event.preventDefault();
    userError = '';
    userSuccess = '';

    if (newPassword.length < 8) {
      userError = 'La password deve essere di almeno 8 caratteri';
      return;
    }

    if (newPassword !== confirmPassword) {
      userError = 'Le password non coincidono';
      return;
    }

    try {
      creatingUser = true;
      const result = await customersApi.createUser(params.id, newPassword);
      console.log('✅ User created:', result);
      userSuccess = 'Account utente creato con successo';
      closeCreateUserModal();
      await loadUserInfo();
    } catch (err) {
      console.error('❌ Create user error:', err);
      userError = err.message || 'Errore durante la creazione dell\'account';
    } finally {
      creatingUser = false;
    }
  }
</script>

<Layout>
  <div class="max-w-5xl mx-auto">
    <!-- Back button -->
    <button
      on:click={goBack}
      class="mb-6 inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
    >
      <ArrowLeft size={20} class="mr-2" />
      <span class="font-medium">Back to Customers</span>
    </button>

    {#if loading}
      <div class="card p-12 text-center">
        <div class="loading-spinner h-12 w-12 mx-auto mb-4"></div>
        <p class="text-lg font-medium text-gray-700 dark:text-gray-300">Loading customer...</p>
      </div>
    {:else if error}
      <div class="card p-12 text-center">
        <div class="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Error loading customer</h3>
        <p class="text-gray-500 dark:text-gray-400">{error}</p>
        <button on:click={goBack} class="mt-4 btn btn-primary">
          Go back
        </button>
      </div>
    {:else if customer}
      <!-- Header -->
      <div class="card p-6 mb-6">
        <div class="flex items-start justify-between">
          <div class="flex items-center space-x-4">
            <div class="w-16 h-16 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
              <User size={32} class="text-white" />
            </div>
            <div>
              <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {getDisplayName()}
              </h1>
              <div class="flex items-center space-x-3 mt-1">
                <span class="font-mono text-sm font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-2 py-1 rounded">
                  {customer.id}
                </span>
                {#if customer.type}
                  <span class="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                    {customer.type}
                  </span>
                {/if}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav class="flex space-x-4" aria-label="Tabs">
          <button
            on:click={() => activeTab = 'info'}
            class="px-4 py-3 text-sm font-medium border-b-2 transition-colors {activeTab === 'info'
              ? 'border-primary-500 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}"
          >
            <div class="flex items-center space-x-2">
              <User size={16} />
              <span>Info</span>
            </div>
          </button>
          <button
            on:click={() => activeTab = 'pricing'}
            class="px-4 py-3 text-sm font-medium border-b-2 transition-colors {activeTab === 'pricing'
              ? 'border-primary-500 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}"
          >
            <div class="flex items-center space-x-2">
              <DollarSign size={16} />
              <span>Custom Pricing</span>
              {#if customerPrices.length > 0}
                <span class="bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 px-2 py-0.5 rounded-full text-xs font-semibold">
                  {customerPrices.length}
                </span>
              {/if}
            </div>
          </button>
          {#if $isAdmin}
            <button
              on:click={() => activeTab = 'account'}
              class="px-4 py-3 text-sm font-medium border-b-2 transition-colors {activeTab === 'account'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}"
            >
              <div class="flex items-center space-x-2">
                <UserCheck size={16} />
                <span>Account Portale</span>
                {#if userInfo?.hasUser}
                  <span class="bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full text-xs font-semibold">
                    Attivo
                  </span>
                {/if}
              </div>
            </button>
          {/if}
        </nav>
      </div>

      <!-- Tab Content -->
      {#if activeTab === 'info'}
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Contact Info -->
          <div class="card p-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <Mail size={18} class="mr-2 text-primary-500" />
              Contact Information
            </h3>
            <dl class="space-y-3">
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Email</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.email || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">PEC</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.pec || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Phone</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.phone || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Destination Code</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100 font-mono">{customer.destinationCode || '-'}</dd>
              </div>
            </dl>
          </div>

          <!-- Fiscal Info -->
          <div class="card p-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <Building2 size={18} class="mr-2 text-primary-500" />
              Fiscal Information
            </h3>
            <dl class="space-y-3">
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">VAT Number</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100 font-mono">{customer.VATNumber || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Fiscal Code</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100 font-mono">{customer.fiscalCode || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">EORI Code</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100 font-mono">{customer.EORICode || '-'}</dd>
              </div>
            </dl>
          </div>

          <!-- Address -->
          <div class="card p-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <MapPin size={18} class="mr-2 text-primary-500" />
              Address
            </h3>
            <dl class="space-y-3">
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Street</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {[customer.address, customer.houseNumber].filter(Boolean).join(', ') || '-'}
                </dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">City</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.municipality || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Province</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.province || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">CAP</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.cap || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Country</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.country || '-'}</dd>
              </div>
            </dl>
          </div>

          <!-- Payment Info -->
          <div class="card p-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <CreditCard size={18} class="mr-2 text-primary-500" />
              Payment Information
            </h3>
            <dl class="space-y-3">
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Beneficiary</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.beneficiary || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Payment Terms</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.paymentTerms || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Payment Method</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.paymentMethod || '-'}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-sm text-gray-500 dark:text-gray-400">Bank</dt>
                <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.Bank || '-'}</dd>
              </div>
            </dl>
          </div>
        </div>
      {:else if activeTab === 'pricing'}
        <div class="card overflow-hidden">
          <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Custom Variant Prices</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">Special pricing configured for this customer</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{$_('customers.prices.inheritHint')}</p>
            </div>
          </div>

          {#if customerPrices.length === 0}
            <div class="p-12 text-center">
              <div class="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <Tag size={32} class="text-gray-400 dark:text-gray-500" />
              </div>
              <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No custom prices</h3>
              <p class="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                This customer doesn't have any custom variant prices configured. Add them from the Products page.
              </p>
            </div>
          {:else}
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Product</th>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Variant</th>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{$_('customers.prices.condition')}</th>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Base Price</th>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Custom Price</th>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Notes</th>
                    <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {#each sortedPrices as price}
                    <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td class="px-4 py-3">
                        <span class="text-sm font-medium text-gray-900 dark:text-white">
                          {price.variant?.product?.name || '-'}
                        </span>
                      </td>
                      <td class="px-4 py-3">
                        <div>
                          <span class="text-sm font-medium text-gray-900 dark:text-white">
                            {price.variant?.name || '-'}
                          </span>
                          <span class="text-xs text-gray-500 dark:text-gray-400 ml-2 font-mono">
                            ({price.variant?.code || price.variantId})
                          </span>
                        </div>
                      </td>
                      <td class="px-4 py-3">
                        <!--
                          Without this the same variant appears up to five
                          times, one row per condition, with nothing to tell
                          them apart. Base keeps the neutral styling; a
                          surcharge gets the same fuchsia badge the timesheet
                          list and modal use for the condition that priced it.
                        -->
                        {#if isBasePrice(price)}
                          <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                            {conditionLabel(price)}
                          </span>
                        {:else}
                          <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900 dark:text-fuchsia-200">
                            {conditionLabel(price)}
                          </span>
                        {/if}
                      </td>
                      <td class="px-4 py-3">
                        <span class="text-sm text-gray-600 dark:text-gray-400">
                          {formatPrice(price.variant?.price)}
                        </span>
                      </td>
                      <td class="px-4 py-3">
                        <span class="text-sm font-semibold text-primary-600 dark:text-primary-400">
                          {formatPrice(price.customPrice)}
                        </span>
                        <!--
                          Base only: a surcharge is an absolute hourly price
                          meant to sit above the list price, so running it
                          through the discount formula renders a negative
                          "% off" in green - the exact opposite of what it is.
                        -->
                        {#if isBasePrice(price) && price.variant?.price}
                          {@const discount = calculateDiscount(price.variant.price, price.customPrice)}
                          {#if discount !== null}
                            <span class="text-xs text-green-600 dark:text-green-400 ml-2">
                              ({discount}% off)
                            </span>
                          {/if}
                        {/if}
                      </td>
                      <td class="px-4 py-3">
                        <span class="text-sm text-gray-600 dark:text-gray-400">
                          {isBasePrice(price) ? (price.notes || '-') : '-'}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-right">
                        <div class="flex items-center justify-end space-x-2">
                          <button
                            on:click={() => openEditPriceModal(price)}
                            class="p-1.5 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                            title="{$_('common.edit')} · {variantLabel(price)} · {conditionLabel(price)}"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            on:click={() => deletePrice(price)}
                            class="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                            title="{$_('common.delete')} · {variantLabel(price)} · {conditionLabel(price)}"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      {:else if activeTab === 'account'}
        <div class="card p-6">
          <div class="flex items-start justify-between mb-6">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <UserCheck size={18} class="mr-2 text-primary-500" />
                Account Portale Cliente
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Gestisci l'accesso al portale cliente per visualizzare e approvare proposte
              </p>
            </div>
          </div>

          {#if loadingUserInfo}
            <div class="text-center py-8">
              <div class="loading-spinner h-8 w-8 mx-auto mb-4"></div>
              <p class="text-gray-500 dark:text-gray-400">Caricamento...</p>
            </div>
          {:else if userInfo?.hasUser}
            <!-- User exists -->
            <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0">
                  <div class="w-12 h-12 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                    <UserCheck size={24} class="text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div class="flex-1">
                  <h4 class="text-lg font-medium text-green-800 dark:text-green-200">Account Attivo</h4>
                  <p class="text-sm text-green-700 dark:text-green-300 mt-1">
                    Questo cliente ha un account per accedere al portale.
                  </p>
                  <dl class="mt-4 space-y-2">
                    <div class="flex items-center text-sm">
                      <dt class="text-green-600 dark:text-green-400 w-24">Email:</dt>
                      <dd class="text-green-800 dark:text-green-200 font-medium">{userInfo.email}</dd>
                    </div>
                    {#if userInfo.createdAt}
                      <div class="flex items-center text-sm">
                        <dt class="text-green-600 dark:text-green-400 w-24">Creato il:</dt>
                        <dd class="text-green-800 dark:text-green-200">
                          {new Date(userInfo.createdAt).toLocaleDateString('it-IT')}
                        </dd>
                      </div>
                    {/if}
                  </dl>
                </div>
              </div>
            </div>
          {:else if customer?.email}
            <!-- No user, but has email -->
            <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0">
                  <div class="w-12 h-12 bg-yellow-100 dark:bg-yellow-800 rounded-full flex items-center justify-center">
                    <Key size={24} class="text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
                <div class="flex-1">
                  <h4 class="text-lg font-medium text-yellow-800 dark:text-yellow-200">Nessun Account</h4>
                  <p class="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    Puoi creare un account per permettere a questo cliente di accedere al portale.
                  </p>
                  <p class="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
                    L'account verrà creato con l'email: <strong>{customer.email}</strong>
                  </p>
                  <button
                    on:click={openCreateUserModal}
                    class="btn btn-primary mt-4"
                  >
                    <Plus size={16} class="mr-2" />
                    Crea Account
                  </button>
                </div>
              </div>
            </div>
          {:else}
            <!-- No email -->
            <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0">
                  <div class="w-12 h-12 bg-red-100 dark:bg-red-800 rounded-full flex items-center justify-center">
                    <AlertCircle size={24} class="text-red-600 dark:text-red-400" />
                  </div>
                </div>
                <div class="flex-1">
                  <h4 class="text-lg font-medium text-red-800 dark:text-red-200">Email Mancante</h4>
                  <p class="text-sm text-red-700 dark:text-red-300 mt-1">
                    Per creare un account portale, il cliente deve avere un indirizzo email configurato.
                  </p>
                  <p class="text-sm text-red-600 dark:text-red-400 mt-2">
                    Modifica i dati del cliente e aggiungi un indirizzo email.
                  </p>
                </div>
              </div>
            </div>
          {/if}

          {#if userSuccess}
            <div class="mt-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p class="text-sm text-green-700 dark:text-green-300">{userSuccess}</p>
            </div>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</Layout>

<!-- Edit Price Modal -->
{#if showPriceModal && editingPrice}
  <div class="modal-backdrop fade-in">
    <div class="modal-content max-w-md w-full scale-in">
      <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">Edit Custom Price</h2>
        <button
          on:click={closePriceModal}
          class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all"
          disabled={savingPrice}
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form on:submit|preventDefault={handleSavePrice}>
        <div class="px-6 py-4 space-y-4">
          {#if priceError}
            <div class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3">
              <div class="text-sm text-red-800 dark:text-red-200">{priceError}</div>
            </div>
          {/if}

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Variant
            </label>
            <input
              type="text"
              value="{editingPrice.variant?.product?.name} > {editingPrice.variant?.name} ({editingPrice.variant?.code})"
              class="input w-full bg-gray-100 dark:bg-gray-700"
              disabled
            />
          </div>

          <!--
            Which of the customer's up-to-five rows for this variant is being
            edited. Read-only: the condition is part of the row's identity
            server-side (the unique key is customer + variant + condition), so
            it is changed by deleting the row and creating another one.
          -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {$_('customers.prices.condition')}
            </label>
            <input
              type="text"
              value={conditionLabel(editingPrice)}
              class="input w-full bg-gray-100 dark:bg-gray-700"
              disabled
            />
          </div>

          <div>
            <label for="customPrice" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Custom Price *
            </label>
            <div class="relative">
              <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">€</span>
              <input
                id="customPrice"
                type="text"
                bind:value={priceFormData.customPrice}
                placeholder="0.00"
                class="input w-full pl-8"
                disabled={savingPrice}
                required
              />
            </div>
            {#if editingPrice.variant?.price}
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Base price: {formatPrice(editingPrice.variant.price)}
              </p>
            {/if}
            {#if !isBasePrice(editingPrice)}
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {$_('products.variants.conditionPrices.hint')}
              </p>
            {/if}
          </div>

          <!--
            Notes belong to the base row, the single place CustomerPricesModal
            writes them; a condition row is a price and nothing else, so the
            field is replaced by the reason it is absent rather than shown and
            silently ignored.
          -->
          {#if isBasePrice(editingPrice)}
            <div>
              <label for="notes" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <input
                id="notes"
                type="text"
                bind:value={priceFormData.notes}
                placeholder="e.g. VIP client"
                class="input w-full"
                disabled={savingPrice}
              />
            </div>
          {:else}
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {$_('customers.prices.notesBaseOnly')}
            </p>
          {/if}
        </div>

        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            type="button"
            on:click={closePriceModal}
            class="btn btn-secondary"
            disabled={savingPrice}
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={savingPrice}
          >
            {#if savingPrice}
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            {/if}
            Update
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Create User Modal -->
{#if showCreateUserModal}
  <div class="modal-backdrop fade-in">
    <div class="modal-content max-w-md w-full scale-in">
      <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">Crea Account Portale</h2>
        <button
          on:click={closeCreateUserModal}
          class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all"
          disabled={creatingUser}
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form on:submit|preventDefault={handleCreateUser}>
        <div class="px-6 py-4 space-y-4">
          {#if userError}
            <div class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3">
              <div class="text-sm text-red-800 dark:text-red-200">{userError}</div>
            </div>
          {/if}

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              type="text"
              value={customer?.email || ''}
              class="input w-full bg-gray-100 dark:bg-gray-700"
              disabled
            />
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              L'email del cliente verrà usata come username
            </p>
          </div>

          <div>
            <label for="newPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password *
            </label>
            <input
              id="newPassword"
              type="password"
              bind:value={newPassword}
              placeholder="Minimo 8 caratteri"
              class="input w-full"
              disabled={creatingUser}
              required
              minlength="8"
            />
          </div>

          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Conferma Password *
            </label>
            <input
              id="confirmPassword"
              type="password"
              bind:value={confirmPassword}
              placeholder="Ripeti la password"
              class="input w-full"
              disabled={creatingUser}
              required
            />
          </div>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            type="button"
            on:click={closeCreateUserModal}
            class="btn btn-secondary"
            disabled={creatingUser}
          >
            Annulla
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={creatingUser}
          >
            {#if creatingUser}
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            {/if}
            Crea Account
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
